import type { AxiosResponse } from 'axios'
import { httpClient, request } from '@/utils/http'

// ===================== 类型定义 =====================

/**
 * 文件信息DTO
 */
export interface CWFileDTO {
  id?: string        // 兼容后端返回的 id
  fileId?: string    // 兼容后端返回的 fileId
  fileName: string
  fileSize: string
  fileType: string
  createTime?: string
  createDate?: string // 兼容后端返回的 createDate
}

/**
 * 业务文件VO
 */
export interface BusinessFileVO {
  /** 云存储ID */
  documentId?: string
  /** 文档类型:如托书、提单、发票、合同、运价、报价单等 */
  documentCategory?: number
  /** 文件名 */
  fileName?: string
  /** 文档类型(pdf/xlsx/png等) */
  fileType?: string
  /** 文件大小(字节) */
  fileSize?: number
  /** 业务ID:业务/运价/报价单/用户 */
  // 雪花主键在浏览器端必须保留字符串，避免 Number 精度丢失。
  businessId?: number | string
  /** 文件来源:0-系统上传、1-第三方导入 */
  sourceType?: number
  /** 备注 */
  remarks?: string
  /** 创建时间 */
  dateCreated?: string
}

/**
 * 文档类型
 */
export enum DocumentCategoryEnum {
  /** 询价 */
  INQUIRY = 1,
  /** 回执任务 */
  RECEIPT_TASK = 2,
  /** 招标附件 */
  TENDER_ATTACHMENT = 4,
  /** 供应商报价单-港前（海运、空运、铁路、港前 报价单 的 港前 区域的附件） */
  SUPPLIER_QUOTATION_PRE_PORT = 5,
  /** 供应商报价单-干线（海运、空运、铁路、快递 报价单 的 干线 区域的附件） */
  SUPPLIER_QUOTATION_TRUNK_LINE = 6,
  /** 供应商报价单-尾程（海运、空运、铁路、尾程 报价单 的 尾程 区域的附件） */
  SUPPLIER_QUOTATION_LAST_MILE = 7
}

/**
 * 上传文件参数
 */
export interface UploadFileParams {
  file: File
  params?: Record<string, any>
}

/**
 * 上传文件并记录参数
 */
export interface UploadAndRecordParams {
  file: File
  params?: Record<string, any>
}

/**
 * 获取文件列表参数
 */
export interface GetFilesParams {
  xmlParam: string
}

/**
 * 获取文件列表（多参数）参数
 */
export interface GetFilesMultiParamParams {
  xmlParams: string[]
}

/**
 * 压缩文件参数
 */
export interface CompressFilesParams {
  fileIds: string[]
  fileName: string
}

/**
 * 下载模板文件参数
 */
export interface DownloadTemplateParams {
  code: string
}

/**
 * 删除文件并删除记录参数
 */
export interface DeleteFileRequest {
  /** 云存储文件ID */
  fileId?: string
  /** 业务ID */
  businessId?: string
  /** 业务文件记录ID */
  businessFileId?: string
}

/**
 * 查询业务文件列表参数
 */
export interface QueryBusinessFileListParams {
  /** 业务ID列表 */
  businessIds: Array<number | string>
  /** 文档类型(如托书、提单、发票、合同、运价、报价单等) */
  documentCategory?: number
}

// ===================== 接口实现 =====================

/**
 * 上传文件
 * @param params 上传参数
 * @returns 文件ID
 */
export function uploadFile(params: UploadFileParams): Promise<string> {
  const formData = new FormData()
  formData.append('file', params.file)
  if (params.params) {
    formData.append('params', JSON.stringify(params.params))
  }

  return request.post<string>('/universal/storage/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 上传文件并记录到业务文件表
 * @param params 上传参数
 * @returns 业务文件记录ID
 */
export function uploadAndRecord(params: UploadAndRecordParams): Promise<number> {
  const formData = new FormData()
  formData.append('file', params.file)

  // 将params中的每个键值对作为独立的表单字段添加
  if (params.params) {
    Object.entries(params.params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(`${key}`, JSON.stringify(value))
      }
    })
  }

  return request.post<number>('/universal/storage/uploadAndRecord', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 查询业务文件列表
 * @param params 查询参数
 * @returns 业务文件列表
 */
export function getBusinessFileList(params: QueryBusinessFileListParams): Promise<BusinessFileVO[]> {
  return request.post<BusinessFileVO[]>('/universal/business/file/list', params)
}

/**
 * 获取文件列表
 * @param params 查询参数
 * @returns 文件列表
 */
export function getFiles(params: GetFilesParams): Promise<CWFileDTO[]> {
  return request.post<CWFileDTO[]>('/universal/storage/files', {
    xmlParam: params.xmlParam
  }, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  })
}

/**
 * 获取文件列表（传多个参数）
 * @param params 查询参数
 * @returns 文件列表
 */
export function getFilesMultiParam(params: GetFilesMultiParamParams): Promise<CWFileDTO[]> {
  return request.post<CWFileDTO[]>('/universal/storage/files/multiParam', params.xmlParams)
}

/**
 * 压缩文件并下载
 * @param params 压缩参数
 * @returns 压缩文件流
 */
export function compressFiles(params: CompressFilesParams): Promise<Blob> {
  return request.get<Blob>('/universal/storage/compress', {
    fileIds: params.fileIds.join(','),
    fileName: params.fileName
  }, {
    responseType: 'blob'
  })
}

/**
 * 删除文件
 * @param fileId 文件ID
 * @returns 删除结果
 */
export function deleteFile(fileId: string): Promise<boolean> {
  return request.post<boolean>(`/universal/storage/deleteFile/${fileId}`)
}

/**
 * 根据文件ID下载文件
 * @param fileId 文件ID
 * @returns 文件流
 */
export function downloadFile(fileId: string): Promise<Blob> {
  return request.get<Blob>(`/universal/storage/findByFileId/${fileId}`, undefined, {
    responseType: 'blob'
  })
}

/**
 * 下载文件并保留响应头，供需要预览或按服务端文件名下载的场景使用。
 *
 * @param fileId 文件 ID
 * @returns 文件内容及响应头
 */
export async function downloadFileWithMetadata(fileId: string): Promise<{
  blob: Blob
  contentType?: string
  contentDisposition?: string
}> {
  const response = await httpClient.get<Blob, AxiosResponse<Blob>>(
    `/universal/storage/findByFileId/${encodeURIComponent(fileId)}`,
    {
      responseType: 'blob',
      preserveResponse: true
    } as any
  )
  return {
    blob: response.data,
    contentType: response.headers?.['content-type'],
    contentDisposition: response.headers?.['content-disposition']
  }
}

/**
 * 根据模板编码下载模板文件
 * @param params 下载模板参数
 * @returns 文件流
 */
export function downloadTemplate(params: DownloadTemplateParams): Promise<Blob> {
  return request.post<Blob>('/universal/storage/downloadTemplate', params, {
    responseType: 'blob'
  })
}

/**
 * 删除文件并删除记录
 * 删除云存储中的文件,同时删除数据库中的业务文件关联记录
 * @param params 删除参数
 * @returns 删除结果
 */
export function deleteFileAndDeleteRecord(params: DeleteFileRequest): Promise<boolean> {
  return request.post<boolean>('/universal/storage/deleteFileAndDeleteRecord', params)
}
